import { addSeconds } from "date-fns";
import {
  IMPERSONATOR_BACKUP_KEY,
  IMPERSONATOR_ORIGIN_KEY,
  LOGIN_TOKEN_LOCAL_STORAGE_KEY,
} from "@rentablo/api";
import { endSessionImpersonation } from "@api/v1/methods";
import { LoginToken } from "@api/v1/model";

export { IMPERSONATOR_BACKUP_KEY };

type TokenOrigin = "session" | "local";

// The auth layer reads the active token sessionStorage-first, then localStorage
// (see getAccessTokenQueryFn). Impersonation must therefore write BOTH storages,
// otherwise a session-stored admin token shadows the impersonated localStorage
// token and the app keeps showing the admin.
const readActiveToken = (): string | null =>
  sessionStorage.getItem(LOGIN_TOKEN_LOCAL_STORAGE_KEY) ??
  localStorage.getItem(LOGIN_TOKEN_LOCAL_STORAGE_KEY);

// Where the active token currently lives — used to restore the admin token to
// the same persistence scope it had (sessionStorage = ephemeral login).
const readActiveTokenOrigin = (): TokenOrigin =>
  sessionStorage.getItem(LOGIN_TOKEN_LOCAL_STORAGE_KEY) ? "session" : "local";

const writeActiveToken = (serializedToken: string) => {
  sessionStorage.setItem(LOGIN_TOKEN_LOCAL_STORAGE_KEY, serializedToken);
  localStorage.setItem(LOGIN_TOKEN_LOCAL_STORAGE_KEY, serializedToken);
};

const clearActiveToken = () => {
  sessionStorage.removeItem(LOGIN_TOKEN_LOCAL_STORAGE_KEY);
  localStorage.removeItem(LOGIN_TOKEN_LOCAL_STORAGE_KEY);
};

const clearStaleBackup = () => {
  localStorage.removeItem(IMPERSONATOR_BACKUP_KEY);
  localStorage.removeItem(IMPERSONATOR_ORIGIN_KEY);
};

/**
 * True while an admin is impersonating another user.
 *
 * Validates that both the backup and active tokens are real token objects with
 * different usernames — a raw string-diff was too weak and caused stale keys
 * left by the old Grails impersonation flow (which never cleared the backup on
 * End) to show the banner for normal users.
 *
 * Self-heals by removing any stale/malformed backup key it finds.
 */
export const isImpersonating = (): boolean => {
  const backupRaw = localStorage.getItem(IMPERSONATOR_BACKUP_KEY);
  if (!backupRaw) return false;

  const activeRaw = readActiveToken();
  if (!activeRaw) {
    // No active session — stale key, clean up.
    clearStaleBackup();
    return false;
  }

  try {
    const backup = JSON.parse(backupRaw) as Partial<{
      access_token: string;
      username: string;
    }>;
    const active = JSON.parse(activeRaw) as Partial<{
      access_token: string;
      username: string;
    }>;

    // Both must look like real tokens.
    if (!backup.access_token || !active.access_token) {
      clearStaleBackup();
      return false;
    }

    // Same access_token → the backup and the active session are the same token,
    // so this is a stale key from a previous session (not an active impersonation).
    // We use access_token rather than username because two different users could
    // theoretically share a username, whereas two different auth sessions always
    // have distinct access tokens.
    if (backup.access_token === active.access_token) {
      clearStaleBackup();
      return false;
    }

    return true;
  } catch {
    // Malformed JSON — stale key.
    clearStaleBackup();
    return false;
  }
};

export const impersonate = (impersonatedUserToken: LoginToken) => {
  const token = {
    ...impersonatedUserToken,
    rememberMe: true,
    expires_at: addSeconds(new Date(), impersonatedUserToken.expires_in),
  };
  // Back up the real admin token (and its storage origin) once; don't overwrite
  // it if a (nested) impersonation is already in progress, so "End" always
  // returns to the admin.
  if (!localStorage.getItem(IMPERSONATOR_BACKUP_KEY)) {
    localStorage.setItem(IMPERSONATOR_BACKUP_KEY, readActiveToken() || "");
    localStorage.setItem(IMPERSONATOR_ORIGIN_KEY, readActiveTokenOrigin());
  }
  writeActiveToken(JSON.stringify(token));
  return token;
};

export const endImpersonation = () => {
  const backedUpToken = localStorage.getItem(IMPERSONATOR_BACKUP_KEY);
  const origin = localStorage.getItem(
    IMPERSONATOR_ORIGIN_KEY
  ) as TokenOrigin | null;
  // Restore the admin token only to the storage it originally came from, so an
  // ephemeral (sessionStorage) login doesn't become persistent in localStorage.
  clearActiveToken();
  if (backedUpToken) {
    const target = origin === "session" ? sessionStorage : localStorage;
    target.setItem(LOGIN_TOKEN_LOCAL_STORAGE_KEY, backedUpToken);
  }
  // Clear the backup so impersonation is no longer detected after ending.
  localStorage.removeItem(IMPERSONATOR_BACKUP_KEY);
  localStorage.removeItem(IMPERSONATOR_ORIGIN_KEY);
  endSessionImpersonation();
};
